import { useCallback } from 'react';
import { BarSeriesOption, LineSeriesOption } from 'echarts';
import { merge } from 'lodash-es';
import { useAtomValue } from 'jotai';
import { YAXisOption } from 'echarts/types/dist/shared';
import {
  getDateRange,
  KpiSeriesBySectionByPeriod,
  useGetFinancialsBySectionAndPeriod,
} from '../../../hooks/useLastThreePeriodData';
import { IField } from '../../../../../data-models/field2.data-model';
import { KpiPeriod, KpiSection } from '../../../../../data-models/company-financials.data-model';
import { FormatterFn, FormatterService } from '../../../../../util/formatter-service';
import { EChartsBarOption } from '../../../../../components/Charts/Barchart/BarChart';
import { selectedMetricsDateCPState } from '../../../state/CompanyFinancialsDateState';
import { selectedCompanyIdProfileAtom } from '../../../state/UIStateJ';
import { perfFrequencyAtom } from '../../../state/FinancialsDateViewPreferences';
import {
  IChartProps,
  MixedChart,
  MixedSeriesOption,
} from '../../../../../components/Charts/MixedChart/MixedChart';
import { AxisTickTextStyle } from '../../../../../components/Charts/chart-constants';
import { companyStateJ } from '../../../../../services/state/CompanyStateJ';
import { endOfFiscalPeriod, getFormattedFiscalDate } from '../../../utils/financialUtils';
import { formatISODateOnly } from '../../../../../util/formatters/DateFormatters';
import { createFormatterDataModel } from '../../../../../data-models/formatter.data-model';
import { getDatesInRange, kpisAsSeries } from './PerfBarChart';

export interface IChartFieldParams {
  field: IField<unknown>;
  kpiSection: KpiSection;
  options: Omit<BarSeriesOption & LineSeriesOption, 'type'> & { type: 'bar' | 'line' };
}

export interface IViewConfig {
  date?: Date;
  frequency?: KpiPeriod;
  periodRange?: number;
}
interface IMixedPerfChartProps {
  title: string;
  fields: IChartFieldParams[];
  viewConfig?: IViewConfig;
  staticOptions?: IChartProps['staticOptions'];
}
export function MixedPerfChart({ fields, viewConfig, staticOptions }: IMixedPerfChartProps) {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const selectedDate = useAtomValue(selectedMetricsDateCPState(companyId))!;
  const selectedPeriod = useAtomValue(perfFrequencyAtom(companyId));
  const fye = useAtomValue(companyStateJ(companyId))?.fye ?? 12;
  const getChartData = useGetMixedPerfChartData();
  const { date = selectedDate, frequency: period = selectedPeriod, periodRange = 4 } = viewConfig ?? {};
  // Snap the date to the correct fiscal period end for the active frequency.
  // On the Performance tab this is already handled by useFiscalDatePicker's initialization,
  // making this a no-op. On the Summary tab (no date picker), this ensures the chart
  // x-axis aligns with fiscal period boundaries even when the raw date is from a
  // different granularity than the preference frequency.
  const snappedDate = endOfFiscalPeriod(date, period, fye - 1);
  const { chartData, timeCategories, yAxis } = getChartData(fields, snappedDate, period, periodRange, fye);

  return (
    <MixedChart
      xCategoryData={timeCategories}
      yValueData={chartData}
      yAxis={yAxis}
      staticOptions={staticOptions}
    />
  );
}

export function useGetMixedPerfChartData() {
  const getFinancials = useGetFinancialsBySectionAndPeriod();

  return useCallback(
    function (
      fields: IChartFieldParams[],
      selectedDate: Date,
      selectedPeriod: KpiPeriod,
      range: number,
      fye: number
    ) {
      const { from, to } = getDateRange(selectedPeriod, selectedDate, range);
      const displayedDates = getDatesInRange(selectedPeriod, selectedDate, range);
      const timeCategories = displayedDates.map((date) =>
        getFormattedFiscalDate(formatISODateOnly(date), selectedPeriod, fye ?? 12)
      );
      const financialsForRange = getFinancials(from, to);

      // The set of y-axis indices the series will render on. Default is 0 (left
      // axis); a series sets `yAxisIndex: 1` to opt into the right axis. With the
      // singleYAxis preference, KPI 2 stays at 0 and this collapses to one axis.
      const seriesAxisIndices = fields.map(({ options }) => (options?.yAxisIndex as number | undefined) ?? 0);
      const usedAxes = new Set(seriesAxisIndices);
      const axisCount = usedAxes.size === 0 ? 1 : Math.max(...usedAxes) + 1;

      // The formatter for each axis is taken from the first series targeting it
      // (so when two series share an axis, the primary KPI's formatter wins).
      // Earlier versions deduped by formatter reference via Set; two fields
      // sharing a cached currency formatter collapsed to one entry, causing a
      // string formatter to land on axis 1 and render raw numbers on the right.
      const formatterByAxis = new Map<number, FormatterFn<unknown>>();
      fields.forEach(({ field, options }) => {
        const idx = (options?.yAxisIndex as number | undefined) ?? 0;
        if (!formatterByAxis.has(idx)) {
          formatterByAxis.set(idx, FormatterService.get().getFormatterForField(field));
        }
      });
      const fallbackFormatter =
        formatterByAxis.get(0) ??
        FormatterService.get().getFormatterForModel(createFormatterDataModel({ type: 'string' }));
      const yAxis = Array.from({ length: axisCount }, (_, i) => ({
        ...AxisTickTextStyle,
        type: 'value',
        // `alignTicks` on the secondary axis tells ECharts to share splitLines/tick
        // counts with the primary axis so left/right gridlines line up.
        ...(i === 1 ? { alignTicks: true } : {}),
        axisLabel: {
          formatter: (val: number) => {
            const fmt = formatterByAxis.get(i) ?? fallbackFormatter;
            return fmt(Number(val));
          },
        },
      })) as YAXisOption[];
      const chartData = fields.map(({ field, kpiSection, options = {} }) => {
        const formatter = FormatterService.get().getFormatterForField(field);

        const formatterOptions: EChartsBarOption = {
          tooltip: {
            valueFormatter: (value) => {
              if (value !== 0 && !value) {
                return '-';
              }
              return formatter?.(Number(value)) ?? '';
            },
          },
        };
        return getSeries({
          field,
          selectedPeriod,
          dataSeries: financialsForRange,
          fye,
          kpiSection,
          ...merge({ ...options }, { ...formatterOptions }),
        });
      });

      return { timeCategories, chartData, yAxis };
    },
    [getFinancials]
  );
}

export type SeriesParams = {
  field: IField<unknown>;
  selectedPeriod: KpiPeriod;
  dataSeries: KpiSeriesBySectionByPeriod;
  fye: number;
  kpiSection: KpiSection;
};

export function getSeries(params: SeriesParams & MixedSeriesOption): MixedSeriesOption {
  const { field, selectedPeriod, dataSeries, fye, kpiSection, ...seriesOptions } = params;
  const series: LineSeriesOption | BarSeriesOption = {
    data: kpisAsSeries(
      dataSeries.get(kpiSection)?.get(selectedPeriod)?.get(field.displayName) ?? [],
      selectedPeriod,
      fye
    ),
    ...seriesOptions,
  };

  return series as MixedSeriesOption;
}
